/**
 * Shared Zod schemas for **root-level** fields on patient-assessment step forms.
 *
 * Every assessment step schema (info, medical, needs, transfer, …) repeats these
 * fields. Import from here instead of redefining them in each `schema.ts`.
 *
 * Profile/patient field shapes live in `profileFieldSchemas.ts`.
 */
import { ASSESSMENT_VARIANTS } from "core/consts";
import type { Solutions, Specializations } from "core/types";
import { z } from "zod";

/**
 * Shared field for `assessment_variant` across all assessment step schemas.
 * Accepts null from RHF defaults; validates against {@link ASSESSMENT_VARIANTS}.
 */
export const assessmentVariantSchema = z.enum(ASSESSMENT_VARIANTS).nullish();

export type AssessmentVariantFormInput = z.input<
  typeof assessmentVariantSchema
>;

const assessmentSpecializationsInputSchema = z
  .union([
    z.array(z.union([z.number(), z.string()])),
    z.number(),
    z.string(),
    z.null(),
  ])
  .optional();

export type AssessmentSpecializationsFormInput = z.input<
  typeof assessmentSpecializationsInputSchema
>;

function normalizeAssessmentSpecializationsInput(
  val: AssessmentSpecializationsFormInput,
): Specializations | null {
  if (val === null || val === undefined) {
    return null;
  }
  if (Array.isArray(val)) {
    const parsed = val
      .map((entry) => parseInt(String(entry), 10))
      .filter((entry) => !Number.isNaN(entry));
    return parsed.length > 0 ? parsed : null;
  }
  if (typeof val === "number") {
    return [val];
  }
  const parsed = parseInt(val, 10);
  return Number.isNaN(parsed) ? null : [parsed];
}

/**
 * Assessment forms may send `specializations` as one id, an array, `null`, or omit it.
 * Parsed output is {@link Specializations} or `null`.
 */
export const assessmentSpecializationsSchema =
  assessmentSpecializationsInputSchema.transform(
    normalizeAssessmentSpecializationsInput,
  );

const assessmentSolutionsInputSchema = z
  .union([z.number(), z.array(z.number()), z.null()])
  .optional();

export type AssessmentSolutionsInput = z.input<
  typeof assessmentSolutionsInputSchema
>;

function toSolutionsArray(val: number | number[]): Solutions {
  return (Array.isArray(val) ? val : [val]) as Solutions;
}

/**
 * Normalizes RHF `solutions` watch values to {@link Solutions}.
 * Empty when missing; matches {@link assessmentSolutionsSchema} array shape.
 */
export function normalizeAssessmentSolutions(
  solutions: AssessmentSolutionsInput,
): Solutions {
  if (solutions == null) return [];
  return toSolutionsArray(solutions);
}

/**
 * Assessment forms may send `solutions` as one id, an array, `null`, or omit it.
 * Parsed output is {@link Solutions} (normalized array) or `undefined`.
 */
export const assessmentSolutionsSchema =
  assessmentSolutionsInputSchema.transform((val): Solutions | undefined => {
    if (val === undefined || val === null) return undefined;
    return toSolutionsArray(val);
  });
